<template>
    <button
        :type="type"
        :disabled="disabled"
        :class="buttonClasses"
        :aria-label="ariaLabel"
    >
        <i :class="icon"></i>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
    icon: string;
    variant?: 'default' | 'tertiary' | 'highlights' | 'inline' | 'ghost' | 'calendarNav';
    size?: 'sm' | 'md' | 'lg' | 'custom';
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
    variant: 'default',
    size: 'md',
    disabled: false,
    type: 'button',
    ariaLabel: 'Icon button'
});

const variantClasses = computed(() => {
    const variants = {
        default: 'text-black hover:bg-gray-50',
        tertiary: 'text-btn-danger hover:bg-red-50',
        highlights: 'text-btn-text-muted hover:bg-gray-50',
        // No hover background/box - for inline icon glyphs flowing within text (accordion toggles, chip remove, etc.)
        inline: 'text-btn-text-muted',
        // Matches legacy ".btn-icon" row-action icons: transparent, icon color-shifts on hover, no hover bg.
        ghost: 'text-gray-500 hover:text-btn-danger-hover',
        // Matches legacy ".dp__arrow_btn_nav" circular date-picker prev/next month buttons.
        calendarNav: '!rounded-full text-btn-text-disabled hover:bg-btn-calendar-hover'
    };
    return variants[props.variant];
});

// 'custom' applies no fixed w/h/text classes so the caller's own `class` prop controls sizing without forcing a square box.
const sizeClasses = computed(() => {
    const sizes = {
        sm: 'w-6 h-6 text-base',
        md: 'w-8 h-8 text-lg',
        lg: 'w-10 h-10 text-xl',
        custom: ''
    };
    return sizes[props.size];
});

const buttonClasses = computed(() => [
    'inline-flex',
    'items-center',
    'justify-center',
    'rounded',
    'focus:outline-none',
    'transition-colors',
    'duration-200',
    variantClasses.value,
    sizeClasses.value,
    props.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
]);
</script>
